import { bytesToNDigits } from '@mail/cross-sizes-utils';
import { getFeature, isFeature } from 'Cloud/Application/FeaturesEs6';
import { IS_MOBILE_BROWSER, IS_WEBVIEW } from 'reactApp/appHelpers/configHelpers';
import { registerExperiment } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import type { VkMusicBundleTariffInfo } from 'reactApp/modules/vkMusic/vkMusic.types';

const vkMusicBundleEnabledRaw: boolean = isFeature('vkmusic-bundle-enabled') || false;
const vkMusicBundlePromoEnabledRaw: boolean = isFeature('vkmusic-bundle-promo-enabled') || false;
const vkMusicBundleFromQueryEnabledRaw: boolean = isFeature('vkmusic-bundle-from-query-enabled') || false;
const vkMusicBundleBannerEnabledRaw: boolean = isFeature('vkmusic-bundle-banner-enabled') || false;

const platformCondition = !IS_MOBILE_BROWSER && !IS_WEBVIEW;

export const vkMusicBundleEnabled = platformCondition && vkMusicBundleEnabledRaw;
export const vkMusicBundlePromoEnabled = vkMusicBundleEnabled && vkMusicBundlePromoEnabledRaw;
export const vkMusicBundleFromQueryEnabled = vkMusicBundleEnabled && vkMusicBundleFromQueryEnabledRaw;
export const vkMusicBundleBannerEnabled = vkMusicBundleEnabled && vkMusicBundleBannerEnabledRaw;

const VK_MUSIC_BUNDLE_TARIFFS_DEFAULT = {
    trial: { id: 'W256GB_1m_vkmusic_bundle_force_trial_2025', quota: 274877906944, usualPrice: 259 },
    usual: {
        id: 'W256GB_1m_vkmusic_bundle_2025',
        quota: 274877906944,
        usualPrice: 259,
        discount: 35,
    },
};

type VkMusicBundleTariffsType = typeof VK_MUSIC_BUNDLE_TARIFFS_DEFAULT;

const vkMusicBundleTariffs: VkMusicBundleTariffsType = getFeature('vkmusic-bundle-tariffs') || VK_MUSIC_BUNDLE_TARIFFS_DEFAULT;

export const vkMusicBundleTrialTariff: VkMusicBundleTariffInfo = {
    ...vkMusicBundleTariffs.trial,
    space: bytesToNDigits(vkMusicBundleTariffs.trial.quota, 3),
    isTrial: true,
};

export const vkMusicBundleUsualTariff: VkMusicBundleTariffInfo = {
    ...vkMusicBundleTariffs.usual,
    space: bytesToNDigits(vkMusicBundleTariffs.usual.quota, 3),
    isTrial: false,
};

// все тарифы с VK Музыкой должны быть перечислены тут, даже старые
// TODO: перенести в онлайнконф после экспов
const allVkMusicBundleTariffsList = getFeature('vkmusic-bundle-all-tariffs') || [
    'W256GB_1m_vkmusic_bundle_2025',
    'W256GB_1m_vkmusic_bundle_trial_2025',
    'W256GB_1m_vkmusic_bundle_force_trial_2025',
];

export const vkMusicBundleTariffList = [
    ...new Set([vkMusicBundleTrialTariff.id, vkMusicBundleUsualTariff.id, ...allVkMusicBundleTariffsList]),
];

export const vkMusicBundleLastPaymentDaysLimit: number = getFeature('vkmusic-bundle-last-payment-days-limit') || 3 * 30;
export const vkMusicBundleQuotaLimit: number = getFeature('vkmusic-bundle-quota-limit') || 80;
export const vkMusicBundleOfferShowDaysInterval: number = getFeature('vkmusic-bundle-show-days-interval') || 7;
export const vkMusicBundleOfferMaxShowCount: number = getFeature('vkmusic-bundle-max-show-count') || 4;

export const vkMusicBundleImagesPreload: { avatars: boolean; cover: boolean } = getFeature('vkmusic-bundle-images-preload') || {
    avatars: true,
    cover: true,
};

export const vkMusicBundleVKIDChecks: {
    social: boolean;
    login: boolean;
    silent: boolean;
    bind: boolean;
} = getFeature('vkmusic-bundle-vkid-checks') || {
    social: true,
    login: true,
    silent: true,
    bind: true,
};

// TODO: перенести в онлайнконф
export const vkMusicBundleLicenseLink: string =
    getFeature('vk-music-bundle-license-link') || 'https://blog.mail.ru/pravila_mail_space_and_vk_music';
export const vkMusicBundleHelpLink: string = getFeature('vk-music-bundle-help-link') || 'https://help.mail.ru/cloud_web/size/music';
export const vkMusicAccountLink: string = getFeature('vk-music-account-link') || 'https://music.vk.ru/profile';

export const vkMusicBundleDebug: boolean = isFeature('vk-music-bundle-debug') || false;

const filterEventByProductId = (productId: string) => vkMusicBundleTariffList.includes(productId);

if (platformCondition) {
    registerExperiment(
        'vkm25',
        'b',
        [
            AnalyticEventNames.VK_MUSIC_OFFER_SHOW,
            AnalyticEventNames.VK_MUSIC_OFFER_CLICK,
            AnalyticEventNames.VK_MUSIC_OFFER_CLOSE,
            AnalyticEventNames.VK_MUSIC_CONFIRM_SHOW,
            AnalyticEventNames.VK_MUSIC_CONFIRM_CONTINUE,
            AnalyticEventNames.VK_MUSIC_CONFIRM_CHOOSE,
            AnalyticEventNames.VK_MUSIC_CONFIRM_CLOSE,
            AnalyticEventNames.VK_MUSIC_ERROR_SHOW,
            AnalyticEventNames.VK_MUSIC_ERROR_BUY,
            AnalyticEventNames.VK_MUSIC_ERROR_CHOOSE,
            AnalyticEventNames.VK_MUSIC_ERROR_CLOSE,
            AnalyticEventNames.VK_MUSIC_SUCCESS_SHOW,
            AnalyticEventNames.VK_MUSIC_SUCCESS_CLICK,
            AnalyticEventNames.VK_MUSIC_SUCCESS_CLOSE,

            // обычное окно оплаты
            AnalyticEventNames.FAST_CHECKOUT_SHOWN,
            AnalyticEventNames.TARIFF_BUY,
            AnalyticEventNames.TARIFF_BACK_CLICK,
            AnalyticEventNames.TARIFF_CLOSE,

            // окно оплаты триала
            AnalyticEventNames.BUY_FAST_CHECKOUT_VIEW,
            AnalyticEventNames.BUY_FAST_CHECKOUT_CLICK_PAY,
            AnalyticEventNames.BUY_FAST_CHECKOUT_BACK,
            AnalyticEventNames.BUY_FAST_CHECKOUT_CLOSE,

            // радары для обоих окон оплаты
            AnalyticEventNames.BILLING_BUY_TARIFF_SUCCESS,
            AnalyticEventNames.BILLING_BUY_TARIFF_ERROR,

            // баннер на лендинге
            AnalyticEventNames.VK_MUSIC_OFFER_LANDING_VIEW,
            AnalyticEventNames.VK_MUSIC_OFFER_LANDING_CLICK,
        ],
        {
            sendXrayArgs: {
                VK_MUSIC_OFFER_SHOW: ({ isTrial, source }) => ({ eventName: `offer-show-${source}-${isTrial ? 'trial' : 'usual'}` }),
                VK_MUSIC_OFFER_CLICK: ({ isTrial, source }) => ({ eventName: `offer-click-${source}-${isTrial ? 'trial' : 'usual'}` }),
                VK_MUSIC_OFFER_CLOSE: ({ isTrial, source }) => ({ eventName: `offer-close-${source}-${isTrial ? 'trial' : 'usual'}` }),

                VK_MUSIC_CONFIRM_CLOSE: ({ source }) => ({ eventName: `confirm-close-${source}` }),
                VK_MUSIC_CONFIRM_SHOW: ({ source }) => ({ eventName: `confirm-show-${source}` }),
                VK_MUSIC_CONFIRM_CONTINUE: ({ source }) => ({ eventName: `confirm-continue-${source}` }),
                VK_MUSIC_CONFIRM_CHOOSE: ({ source }) => ({ eventName: `confirm-choose-${source}` }),

                VK_MUSIC_ERROR_SHOW: ({ type, source }) => ({ eventName: `error-show-${source}-${type}` }),
                VK_MUSIC_ERROR_BUY: ({ type, source }) => ({ eventName: `error-buy-${source}-${type}` }),
                VK_MUSIC_ERROR_CHOOSE: ({ type, source }) => ({ eventName: `error-choose-${source}-${type}` }),
                VK_MUSIC_ERROR_CLOSE: ({ type, source }) => ({ eventName: `error-close-${source}-${type}` }),

                VK_MUSIC_SUCCESS_SHOW: ({ source }) => ({ eventName: `success-show-${source}` }),
                VK_MUSIC_SUCCESS_CLICK: ({ type, source }) => ({ eventName: `success-click-${source}-${type}` }),
                VK_MUSIC_SUCCESS_CLOSE: ({ source }) => ({ eventName: `success-close-${source}` }),

                FAST_CHECKOUT_SHOWN: ({ productId: id }) => {
                    if (!filterEventByProductId(id)) {
                        return { cancel: true };
                    }
                    return { eventName: `payment-show-usual` };
                },

                TARIFF_BUY: ({ product: { id } }) => {
                    if (!filterEventByProductId(id)) {
                        return { cancel: true };
                    }
                    return { eventName: `payment-click-usual` };
                },

                TARIFF_BACK_CLICK: ({ product: { id } }) => {
                    if (!filterEventByProductId(id)) {
                        return { cancel: true };
                    }
                    return { eventName: `payment-click-back-usual` };
                },

                TARIFF_CLOSE: ({ product: { id } }) => {
                    if (!filterEventByProductId(id)) {
                        return { cancel: true };
                    }
                    return { eventName: `payment-close-usual` };
                },

                BUY_FAST_CHECKOUT_VIEW: ({ product: { id } }) => {
                    if (!filterEventByProductId(id)) {
                        return { cancel: true };
                    }
                    return { eventName: `payment-show-trial` };
                },

                BUY_FAST_CHECKOUT_CLICK_PAY: ({ product: { id } }) => {
                    if (!filterEventByProductId(id)) {
                        return { cancel: true };
                    }
                    return { eventName: `payment-click-trial` };
                },

                BUY_FAST_CHECKOUT_BACK: ({ product: { id } }) => {
                    if (!filterEventByProductId(id)) {
                        return { cancel: true };
                    }
                    return { eventName: `payment-click-back-trial` };
                },

                BUY_FAST_CHECKOUT_CLOSE: ({ product: { id } }) => {
                    if (!filterEventByProductId(id)) {
                        return { cancel: true };
                    }
                    return { eventName: `payment-close-trial` };
                },

                BILLING_BUY_TARIFF_SUCCESS: ({ productId: id, status, isTrial }) => {
                    if (!filterEventByProductId(id)) {
                        return { cancel: true };
                    }
                    if (isTrial) {
                        return { eventName: `trial-success-${status}` };
                    }
                    return { eventName: `buy-success-${status}` };
                },
                BILLING_BUY_TARIFF_ERROR: ({ productId: id, status, isTrial }) => {
                    if (!filterEventByProductId(id)) {
                        return { cancel: true };
                    }
                    if (isTrial) {
                        return { eventName: `trial-error-${status}` };
                    }
                    return { eventName: `buy-error-${status}` };
                },
            },
            mapEventNames: {
                VK_MUSIC_OFFER_LANDING_VIEW: 'landing-banner-view',
                VK_MUSIC_OFFER_LANDING_CLICK: 'landing-banner-click',
            },
        }
    );
}
